/**
 * Footer disclaimer (4-surface AC-5 - landing footer surface)
 *
 * Lang-conditional render per BUG-2026-05-05-02 fix #4:
 * legal-upl HARD VETO requires disclaimer render in user's chosen
 * language only - mixed VN+EN weakens "user understood disclaimer"
 * presumption (LEGAL-DIY Sec.3 sticky disclaimer 4-surface). Verbatim
 * copy still matches LEGAL-TEMPLATE-HASH chat_persistent_disclaimer_{vn|en};
 * we just gate which one renders via useLang().
 *
 * 2026-05-22 (Direction A "Tu phap restraint"): added locked voice
 * statement prelude (BRAND.md s4.0) + 4-luat citation block per
 * BRAND.md s4.3 + CLAUDE.md "Blog disclaimer block bat buoc".
 */

import { useLang } from '../use-lang';

// Exported for reuse by FooterAnchor (Editorial Authority landing). These are
// LEGAL-TEMPLATE-HASH locked strings (legal-upl HARD) - reuse VERBATIM, never
// retype or fork the wording.
export const VOICE_STATEMENT_VN =
  'Asklaw cung cấp thông tin pháp luật Việt Nam từ kho VBPL chính thức, ' +
  'kèm trích dẫn Điều/Khoản, với độ tin cậy 95%+ - KHÔNG thay thế luật sư.';

export const VOICE_STATEMENT_EN =
  'Asklaw provides Vietnamese legal information from the official VBPL corpus, ' +
  'with Article/Clause citations, at 95%+ accuracy - NOT a substitute for a lawyer.';

export const DISCLAIMER_VN =
  'Asklaw chỉ cung cấp thông tin tham khảo từ kho VBPL chính thức, KHÔNG phải tư vấn pháp lý. ' +
  'Không tạo lập quan hệ luật sư - khách hàng giữa Asklaw và người dùng. ' +
  'Vui lòng liên hệ luật sư có chứng chỉ để được tư vấn cho vụ việc cụ thể. ' +
  'Tuân thủ: Luật Luật sư 65/2006/QH11, Luật 20/2012/QH13 sửa đổi Luật Luật sư, ' +
  'Luật Trí tuệ nhân tạo 2026, Luật Bảo vệ dữ liệu cá nhân 2026.';

export const DISCLAIMER_EN =
  'Asklaw only provides reference information from the official VBPL corpus, NOT legal advice. ' +
  'No attorney-client relationship is created between Asklaw and the user. ' +
  'Please consult a licensed lawyer for advice on specific matters. ' +
  'Compliance: Law on Lawyers 65/2006/QH11, Law 20/2012/QH13 amending the Law on Lawyers, ' +
  'Vietnam AI Act 2026, Personal Data Protection Law 2026.';

// AI-imagery disclosure (legal-upl CAVEAT-PASS VETO-2026-06-11-LANDING-AI-IMAGERY-LABEL):
// Luat AI 2026 labeling + EU AI Act Art.50 transparency cho synthetic media +
// khu rui ro testimonial gia. Wording LOCKED - reuse verbatim.
export const IMG_AI_NOTE_VN =
  'Hình ảnh trên trang chỉ mang tính minh hoạ và được tạo bởi AI, không phải ảnh người thật hay khách hàng thật.';
export const IMG_AI_NOTE_EN =
  'Images on this page are illustrative and AI-generated; they do not depict real persons or real clients.';

export function Footer() {
  const { lang } = useLang();

  const t = {
    nav: lang === 'vi' ? 'Footer' : 'Footer',
    about: lang === 'vi' ? 'Về Asklaw' : 'About',
    contact: lang === 'vi' ? 'Liên hệ' : 'Contact',
    editorial: lang === 'vi' ? 'Tiêu chuẩn biên tập' : 'Editorial standards',
    news: lang === 'vi' ? 'Tin pháp luật' : 'News',
    privacy: lang === 'vi' ? 'Bảo mật' : 'Privacy',
    terms: lang === 'vi' ? 'Điều khoản' : 'Terms',
    dpo: 'DPO',
    copyright: lang === 'vi' ? '(c) Asklaw - Sinh bởi AI Asklaw' : '(c) Asklaw - Generated by Asklaw AI',
  };

  return (
    <footer
      role="contentinfo"
      style={{
        maxWidth: 'var(--max-width)',
        margin: '64px auto 24px',
        padding: '24px 16px',
        borderTop: '1px solid var(--color-border)',
        color: 'var(--color-muted)',
        fontSize: 13,
      }}
    >
      <nav
        aria-label={t.nav}
        style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}
      >
        <a href="/about">{t.about}</a>
        <a href="/contact">{t.contact}</a>
        <a href="/editorial-standards">{t.editorial}</a>
        <a href="/news">{t.news}</a>
        <a href="/privacy">{t.privacy}</a>
        <a href="/terms">{t.terms}</a>
        <a href="mailto:dpo@asklaw.vn">{t.dpo}</a>
      </nav>
      {lang === 'vi' ? (
        <>
          <p
            style={{
              marginTop: 16,
              fontWeight: 600,
              color: 'var(--asklaw-ink, #0e2a52)',
            }}
            lang="vi"
          >
            {VOICE_STATEMENT_VN}
          </p>
          <p style={{ marginTop: 8 }} lang="vi">
            {DISCLAIMER_VN}
          </p>
        </>
      ) : (
        <>
          <p
            style={{
              marginTop: 16,
              fontWeight: 600,
              color: 'var(--asklaw-ink, #0e2a52)',
            }}
            lang="en"
          >
            {VOICE_STATEMENT_EN}
          </p>
          <p style={{ marginTop: 8 }} lang="en">
            {DISCLAIMER_EN}
          </p>
        </>
      )}
      <p style={{ marginTop: 8 }}>{t.copyright}</p>
    </footer>
  );
}
